import React, { FC } from "react";
import { observer } from "mobx-react";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import DatePicker from "@mindmemobile/mm-date-picker";
import GlobeIcon from "../../../../../../images/common/globe-icon.svg";
import UserNameIcon from "../../../../../../images/common/T-grey.svg";
import MessageIcon from "../../../../../../images/common/message-grey.svg";
import DraggableIcon from "../../../../../../images/products/draggable-grey.svg";
import CallIcon from "../../../../../../images/common/call-grey.svg";
// import CalendarIcon from '../../../../../../images/common/calendar-grey.svg';
import CakeIcon from "../../../../../../images/common/cake-grey.svg";
// import { ReactSortable } from "react-sortablejs";
import {
  CheckoutPaymentFormModel,
  CheckoutPaymentPageModel,
  CheckOutPayPageFormFields,
} from "../../../shared/models";
import {
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  SingleSelect,
} from "@mindmemobile/mm-shared-global";
import {
  CheckOutPayButtonCaption,
  CheckOutPayFields,
} from "../../../shared/enums";
import SortablePlugin from "../../../shared/components/SortablePlugin";

export const sampleCustomFields: CheckOutPayPageFormFields[] = [];

type IProps = {
  formData: CheckoutPaymentFormModel;
  onChange: (
    field: keyof CheckoutPaymentPageModel,
    value: CheckoutPaymentFormModel
  ) => void;
  sendAction?: (action: keyof CheckoutPaymentFormModel) => void;
};

const buttonOptions: IOption[] = Object.keys(CheckOutPayButtonCaption).map(
  (key) => {
    return {
      label:
        CheckOutPayButtonCaption[key as keyof typeof CheckOutPayButtonCaption],
      value: key,
    };
  }
);
const CheckoutFormSection: FC<IProps> = ({
  formData,
  onChange,
  sendAction,
}) => {
  const handleInputChange = (
    value: string | boolean | Date | CheckOutPayPageFormFields[],
    field: keyof CheckoutPaymentFormModel
  ) => {
    if (field === "isBillingAddressChecked" && value === true) {
      sendAction && sendAction("isBillingAddressChecked");
    }
    if (field === "isShippingAddressChecked" && value === true) {
      sendAction && sendAction("isShippingAddressChecked");
    }
    if (field === "payButtonType") {
      sendAction && sendAction("payButtonType");
    }
    if (field === "formFields") {
      sendAction && sendAction("formFields");
    }
    const updatedForm = new CheckoutPaymentFormModel({
      ...formData,
      [field]: value,
    });
    onChange("formData", updatedForm);
  };

  const handleDeleteField = (index: number) => {
    const clonedFormFields = formData.formFields.map(
      (item) => new CheckOutPayPageFormFields(item)
    );
    clonedFormFields.splice(index, 1);
    handleInputChange(clonedFormFields, "formFields");
  };

  const renderTextInput = (
    field: keyof typeof CheckOutPayFields | string,
    placeholder: string,
    error: boolean,
    errorMessage?: string,
    imgSrc?: string
  ) => (
    <div className="input-addon-wrapper">
      <FlexContainer spacing={15}>
        <div className="input-addon-icon">
          <Input
            type="text"
            value={""}
            onChange={() => ""}
            placeholder={placeholder}
            className={`styled-input merchant-inputs `}
            error={error}
            errorMessage={errorMessage}
          />
          <img src={imgSrc} className="t-img" />
        </div>
      </FlexContainer>
    </div>
  );

  const handleCheckboxChange = (
    field: keyof typeof CheckOutPayFields | string,
    value: boolean
  ) => {
    const clonedFormFields = formData.formFields.map(
      (item) => new CheckOutPayPageFormFields(item)
    );
    const fieldToBeUpdated = formData.formFields.findIndex(
      (item: CheckOutPayPageFormFields) => item.field === field
    );
    clonedFormFields[fieldToBeUpdated].isRequired = value;
    handleInputChange(clonedFormFields, "formFields");
  };

  const renderCheckboxInput = (
    field: keyof typeof CheckOutPayFields | string,
    index: number,
    disabled: boolean = false
  ) => (
    <Input
      type="checkbox"
      value={formData.formFields[index].isRequired}
      onChange={(e) => handleCheckboxChange(field, e.target.checked)}
      disabled={disabled}
      label=""
    />
  );

  const checkBoxInput = (
    key: keyof CheckoutPaymentFormModel,
    value: boolean,
    label: string,
    disabled: boolean
  ) => {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => handleInputChange(e.target.checked, key)}
        label={label}
        disabled={disabled || false}
      />
    );
  };

  const singleSelect = (
    field: keyof CheckoutPaymentFormModel,
    selectedKey: string,
    className: string,
    options: CheckOutPayPageFormFields[] | IOption[],
    error: boolean,
    errorMessage: string
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options]}
        errorMessage={errorMessage}
        onChange={(option: any) => handleInputChange(option.value, field)}
        className={`dpdn-no-separator ${className}`}
      />
    );
  };

  const renderAddressSection = () => {
    return (
      <FlexContainer spacing={20} fxLayout="column" margin="20px 0px">
        <CustomLabel fontWeight={"bold"}>Address</CustomLabel>
        {checkBoxInput(
          "isBillingAddressChecked",
          formData.isBillingAddressChecked,
          "Collect Billing Address",
          false
        )}
        {checkBoxInput(
          "isShippingAddressChecked",
          formData.isShippingAddressChecked,
          "Collect Shipping Address",
          false
        )}
      </FlexContainer>
    );
  };

  const renderButtonSection = () => {
    return (
      <FlexContainer fxLayout="column">
        <CustomLabel fontWeight={"bold"}>Button</CustomLabel>
        <FlexContainer alignItems="center" spacing={10} margin="20px 0px 0px">
          {singleSelect(
            "payButtonType",
            formData.payButtonType,
            "seller-terms-dropdown",
            buttonOptions,
            formData.hasError("payButtonType"),
            ""
          )}
          {formData.payButtonType === CheckOutPayButtonCaption.Custom && (
            <Input
              type={"text"}
              value={formData.buttonValue}
              error={formData.hasError("buttonValue")}
              className={`styled-input merchant-inputs`}
              onChange={(e) => handleInputChange(e.target.value, "buttonValue")}
              placeholder={"Enter value"}
            />
          )}
        </FlexContainer>
      </FlexContainer>
    );
  };

  const renderInputs = (field: string) => {
    switch (field) {
      case CheckOutPayFields.FirstLastName:
        return (
          <FlexContainer spacing={10} className="username-input">
            {renderTextInput(
              CheckOutPayFields.FirstLastName,
              "First Name",
              false,
              "",
              UserNameIcon
            )}
            {renderTextInput(
              CheckOutPayFields.FirstLastName,
              "Last Name",
              false,
              "",
              UserNameIcon
            )}
          </FlexContainer>
        );
      case CheckOutPayFields.EmailAddress:
        return renderTextInput(
          CheckOutPayFields.EmailAddress,
          "Email Address",
          false,
          "",
          MessageIcon
        );
      case CheckOutPayFields.MobileNumber:
        return renderTextInput(
          CheckOutPayFields.MobileNumber,
          "Mobile Number",
          false,
          "",
          CallIcon
        );
      case CheckOutPayFields.Birthday:
      case CheckOutPayFields.RenewalDate:
        return <DatePicker />;
      default:
        return renderTextInput(
          field,
          field === CheckOutPayFields.TaxId ? "Tax Id" : field,
          false
        );
    }
  };

  const renderCheckbox = (field: CheckOutPayPageFormFields, index: number) => {
    if (
      field.field === CheckOutPayFields.Birthday ||
      field.field === CheckOutPayFields.RenewalDate ||
      field.field === CheckOutPayFields.TaxId
    ) {
      return (
        <FlexContainer
          alignItems="center"
          justifyContent="flex-end"
          spacing={22}
        >
          {renderCheckboxInput(field.field, index, false)}
          <img
            src={DeleteIcon}
            alt="delete"
            onClick={() => handleDeleteField(index)}
          />
        </FlexContainer>
      );
    }
    return renderCheckboxInput(
      field.field,
      index,
      field.field === CheckOutPayFields.FirstLastName
    );
  };

  const renderFormFields = () => {
    const handleSortEnd = (sortedKeys: string[]) => {
      const sortedFields = sortedKeys
        .map((key) => formData.formFields.find((field) => field.field === key))
        .filter((field): field is CheckOutPayPageFormFields => !!field);
      handleInputChange(sortedFields, "formFields");
    };

    return (
      <SortablePlugin onSortEnd={handleSortEnd} animation={150}>
        {formData.formFields.map((field, index) => (
          <div
            key={field.field} // React's internal key (still needed for virtual DOM)
            data-key={field.field} // Custom key for sorting
            className="pay-field-chkbx"
            style={{ paddingBottom: "15px" }}
          >
            <div className="drag-drop-icon">
              <img src={DraggableIcon} alt="drag-icon" />
            </div>
            <div
              className={`pay-inputs form-field-input-wrapper ${
                field.field === CheckOutPayFields.Birthday ||
                field.field === CheckOutPayFields.RenewalDate
                  ? "pay-datepicker"
                  : ""
              }`}
            >
              {renderInputs(field.field)}
            </div>
            <div className="input-hint-icons">
              {field.field === CheckOutPayFields.MobileNumber && (
                <img src={GlobeIcon} alt="globe-icon" />
              )}
              {field.field === CheckOutPayFields.Birthday && (
                <img src={CakeIcon} alt="cake-icon" />
              )}
            </div>
            <div className="req-chkbx">{renderCheckbox(field, index)}</div>
          </div>
        ))}
      </SortablePlugin>
    );
  };

  return (
    <>
      <div className="pay-field-chkbx">
        <CustomLabel className="pay-inputs pay-field-margin">
          Fields
        </CustomLabel>
        <CustomLabel className="req-chkbx pay-field-margin">
          Required
        </CustomLabel>
      </div>

      {renderFormFields()}
      {/* <div className="pay-field-chkbx">
                  <div className="drag-drop-icon">
                      <i className="fa fa-solid fa-bars"></i>
                  </div>
                  <div className="pay-inputs">
                      <SingleSelect
                          selectedKey=""
                          searchable={true}
                          nameKeys={['field']}
                          valueKeys={['field']}
                          options={[sampleCustomFields || []]}
                          onChange={(option: any) => handleAddField(option)}
                      />
                  </div>
                  <div className="input-hint-icons"></div>
                  <div className="req-chkbx"></div>
              </div> */}

      {renderAddressSection()}
      {renderButtonSection()}
    </>
  );
};

export default observer(CheckoutFormSection);
